
import { useState, useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Card from '../../components/base/Card';
import Tag from '../../components/base/Tag';
import SearchBar from '../../components/base/SearchBar';
import Button from '../../components/base/Button';
import { supabase } from '../../lib/supabase';

export default function Links() {
  const [activeTab, setActiveTab] = useState('便利リンク');
  const [links, setLinks] = useState([]);
  const [filteredLinks, setFilteredLinks] = useState([]);
  const [sortBy, setSortBy] = useState('new');
  const [searchQuery, setSearchQuery] = useState('');

  // SEO設定
  useEffect(() => {
    document.title = "リンク集 - 兵庫県政検証サイト";

    // メタタグの設定
    let descriptionMeta = document.querySelector('meta[name="description"]');
    if (!descriptionMeta) {
      descriptionMeta = document.createElement('meta');
      descriptionMeta.setAttribute('name', 'description');
      document.head.appendChild(descriptionMeta);
    }
    descriptionMeta.setAttribute('content', '情報置き場や便利なまとめ、連載記事などブックマーク推奨リンクをカテゴリ別にリストアップ。マスコミ報道、YouTube動画、X投稿、専門家見解など幅広く収集。');

    // Schema.org JSON-LD for Links Page
    const linksPageSchema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "リンク集",
      "description": "情報置き場や便利なまとめ、連載記事などブックマーク推奨リンクをカテゴリ別にリストアップ",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/links`,
      "inLanguage": "ja",
      "isPartOf": {
        "@type": "WebSite",
        "name": "兵庫県政検証サイト",
        "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/`
      },
      "mainEntity": {
        "@type": "ItemList",
        "name": "兵庫県政関連リンク集",
        "description": "県政に関する信頼できる情報源のリンク集"
      }
    };

    let existingScript = document.querySelector('script[data-schema="links"]');
    if (existingScript) {
      existingScript.remove();
    }

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-schema', 'links');
    script.textContent = JSON.stringify(linksPageSchema);
    document.head.appendChild(script);

    return () => {
      const scriptToRemove = document.querySelector('script[data-schema="links"]');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, []);

  // カテゴリーリスト
  const categories = [
    '便利リンク',
    'マスコミ報道', 
    'Youtube動画',
    'X投稿',
    '識者・専門家の見解',
    '情報まとめ系',
    '連載'
  ];

  // Supabaseからデータを取得
  useEffect(() => {
    const fetchLinks = async () => {
      try {
        const { data, error } = await supabase
          .from('links')
          .select('*')
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Links fetch error:', error);
        } else {
          setLinks(data || []);
          updateFilteredData(data || [], activeTab);
        }
      } catch (error) {
        console.error('Data fetch error:', error);
      }
    };

    fetchLinks();

    // リアルタイム更新の設定
    const subscription = supabase
      .channel('links_changes')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'links' },
        () => fetchLinks()
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const updateFilteredData = (currentLinks, tab = activeTab) => {
    const categoryLinks = currentLinks.filter(link => link.category === tab);
    setFilteredLinks(categoryLinks);
  };

  // データが更新された時にフィルタリングを再実行
  useEffect(() => {
    updateFilteredData(links, activeTab);
  }, [links, activeTab]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    filterLinks(query, sortBy);
  };

  const handleSortChange = (newSortBy: string) => {
    setSortBy(newSortBy);
    filterLinks(searchQuery, newSortBy);
  };

  const filterLinks = (query: string, sort: string) => {
    let filtered = links.filter(link => link.category === activeTab);

    if (query) {
      filtered = filtered.filter(link =>
        link.title.toLowerCase().includes(query.toLowerCase()) ||
        link.summary.toLowerCase().includes(query.toLowerCase()) ||
        link.comment?.toLowerCase().includes(query.toLowerCase())
      );
    }

    switch (sort) {
      case 'new':
        filtered.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        break;
      case 'popular':
        filtered.sort((a, b) => b.clicks - a.clicks);
        break;
      case 'featured':
        filtered.sort((a, b) => (b.is_featured ? 1 : 0) - (a.is_featured ? 1 : 0));
        break;
    }

    setFilteredLinks(filtered);
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    setSearchQuery('');
    setSortBy('new');
    updateFilteredData(links, tab);
  };

  const currentCategoryLinks = filteredLinks.length > 0 ? filteredLinks : links.filter(link => link.category === activeTab);

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">リンク集</h1>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            情報置き場や便利なまとめ、連載記事などブックマーク推奨リンクをカテゴリ別にリストアップ
          </p>
        </div>

        {/* Category Tab Navigation */}
        <div className="mb-8">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="-mb-px flex space-x-8 overflow-x-auto">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => handleTabChange(category)}
                  className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                    activeTab === category
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  {category}
                  <span className="ml-2 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 px-2 py-0.5 rounded-full text-xs">
                    {links.filter(link => link.category === category).length}
                  </span>
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 mb-8">
          <div className="flex flex-col lg:flex-row gap-6">
            <div className="flex-1">
              <SearchBar
                placeholder="リンクを検索..."
                onSearch={handleSearch}
                className="w-full"
              />
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">並び替え:</span>
              <div className="flex space-x-2">
                {[
                  { value: 'new', label: '新着' },
                  { value: 'popular', label: '人気' },
                  { value: 'featured', label: '注目' }
                ].map((option) => (
                  <Button
                    key={option.value}
                    variant={sortBy === option.value ? 'primary' : 'outline'}
                    size="sm"
                    onClick={() => handleSortChange(option.value)}
                  >
                    {option.label}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="mb-6">
          <p className="text-gray-600 dark:text-gray-400">
            {currentCategoryLinks.length}件のリンクが見つかりました
          </p>
        </div>

        {/* Links Display */}
        <div className="mb-12">
          {/* Image Display Links */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {currentCategoryLinks
              .filter(link => link.display_type === 'image' || !link.display_type)
              .map((link) => (
                <Card key={link.id} className="overflow-hidden hover:shadow-lg transition-all duration-200 cursor-pointer">
                  <div className="aspect-video bg-gray-200 dark:bg-gray-700">
                    <img 
                      src={link.thumbnail} 
                      alt={link.title}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <Tag variant="category">{link.category}</Tag>
                      <div className="flex items-center space-x-2">
                        {link.is_featured && (
                          <i className="ri-star-fill text-yellow-500"></i>
                        )}
                      </div>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 line-clamp-2">
                      <a 
                        href={link.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="hover:text-blue-600 dark:hover:text-blue-400"
                      >
                        {link.title}
                      </a>
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 text-sm mb-4 line-clamp-3">
                      {link.summary}
                    </p>
                    {link.comment && (
                      <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-md mb-4">
                        <p className="text-sm text-blue-800 dark:text-blue-200">
                          <i className="ri-chat-quote-line mr-1"></i>
                          {link.comment}
                        </p>
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <div>
                        {link.source_name.startsWith('http://') || link.source_name.startsWith('https://') ? (
                          <a 
                            href={link.source_name} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                          >
                            {new URL(link.source_name).hostname}
                          </a>
                        ) : (
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            {link.source_name}
                          </span>
                        )}
                      </div>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {new Date(link.created_at).toLocaleDateString('ja-JP')}
                      </span>
                    </div>
                  </div>
                </Card>
              ))}
          </div>

          {/* Text Display Links */}
          <div className="space-y-4">
            {currentCategoryLinks
              .filter(link => link.display_type === 'text')
              .map((link, index) => (
                <div key={link.id} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-all duration-200">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <span className="text-sm font-medium text-gray-500 dark:text-gray-400">#{index + 1}</span>
                        <Tag variant="category">{link.category}</Tag>
                        {link.is_featured && (
                          <i className="ri-star-fill text-yellow-500"></i>
                        )}
                        <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                          テキスト表示
                        </span>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                        <a 
                          href={link.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="hover:text-blue-600 dark:hover:text-blue-400"
                        >
                          {link.title}
                        </a>
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400 text-sm mb-3">
                        {link.summary}
                      </p>
                      {link.comment && (
                        <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-md mb-3">
                          <p className="text-sm text-blue-800 dark:text-blue-200">
                            <i className="ri-chat-quote-line mr-1"></i>
                            {link.comment}
                          </p>
                        </div>
                      )}
                      <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
                        {link.source_name.startsWith('http://') || link.source_name.startsWith('https://') ? (
                          <a 
                            href={link.source_name} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-600 dark:text-blue-400 hover:underline"
                          >
                            {new URL(link.source_name).hostname}
                          </a>
                        ) : (
                          <span className="text-gray-600 dark:text-gray-400">
                            {link.source_name}
                          </span>
                        )}
                        <span>{new Date(link.created_at).toLocaleDateString('ja-JP')}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>

        {currentCategoryLinks.length === 0 && (
          <div className="text-center py-12">
            <i className="ri-search-line text-6xl text-gray-300 dark:text-gray-600 mb-4"></i>
            <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">
              該当するリンクが見つかりませんでした
            </h3>
            <p className="text-gray-500 dark:text-gray-400">
              検索条件を変更してもう一度お試しください
            </p>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
